import { useSelectedLocation } from 'contexts';
import useTimeZone from 'hooks/use-timezone';
import {
  createContext,
  Dispatch,
  FC,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { TextFieldRef } from 'components/inputs/text-field';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { ActiveUnit, Customer, Location, Redelivery, RedeliveryDetail, TruckingCompany } from 'types';
import useRedeliveryApi from './use-redelivery-api';

class RedeliveryState {
  getRedeliveryObject: () => Redelivery = () => ({} as any);
  id?: number;
  loading: boolean = false;
  original: Partial<Redelivery> = {};
  redeliveryNumberRef: RefObject<TextFieldRef> = { current: null };
  customer?: Customer;
  setCustomer: Dispatch<SetStateAction<Customer | undefined>> = () => {};
  expirationDate: string = '';
  setExpirationDate: Dispatch<SetStateAction<string>> = () => {};
  location?: Location | null;
  setLocation: (location: Location) => void = () => {};
  notes?: string;
  setNotes: Dispatch<SetStateAction<string | undefined>> = () => {};
  redeliveryDetails: Map<number, RedeliveryDetail> = new Map();
  setRedeliveryDetails: Dispatch<SetStateAction<Map<number, RedeliveryDetail>>> = () => {};
  redeliveryNumber: string = '';
  setRedeliveryNumber: Dispatch<SetStateAction<string>> = () => {};
  startDate: string = '';
  setStartDate: Dispatch<SetStateAction<string>> = () => {};
  truckingCompanies: Map<number, TruckingCompany> = new Map();
  setTruckingCompanies: Dispatch<SetStateAction<Map<number, TruckingCompany>>> = () => {};
  units: Map<number, ActiveUnit> = new Map();
  setUnits: Dispatch<SetStateAction<Map<number, ActiveUnit>>> = () => {};
}

export const RedeliveryContext = createContext(new RedeliveryState());

export const RedeliveryProvider: FC = () => {
  const { pathname } = useLocation();
  const params = useParams();
  const { getRedelivery } = useRedeliveryApi();
  const { selectedLocation, setSelectedLocation } = useSelectedLocation();
  const { newZonedDayJs } = useTimeZone();

  const id = useMemo(() => Number(params.id), [params.id]);
  const [loading, setLoading] = useState<boolean>(false);
  const [original, setOriginal] = useState<Partial<Redelivery>>({});
  const redeliveryNumberRef = useRef<TextFieldRef>(null);

  const [customer, setCustomer] = useState<Customer>();
  const [expirationDate, setExpirationDate] = useState<string>(newZonedDayJs().add(1, 'month').toISOString());
  const [notes, setNotes] = useState<string>();
  const [redeliveryDetails, setRedeliveryDetails] = useState<Map<number, RedeliveryDetail>>(new Map());
  const [redeliveryNumber, setRedeliveryNumber] = useState<string>('');
  const [startDate, setStartDate] = useState<string>(newZonedDayJs().toISOString());
  const [truckingCompanies, setTruckingCompanies] = useState<Map<number, TruckingCompany>>(new Map());
  const [units, setUnits] = useState<Map<number, ActiveUnit>>(new Map());

  useEffect(() => {
    if (isNaN(id)) return;
    setLoading(true);
    getRedelivery(id)
      .then(redelivery => {
        setOriginal(redelivery);
        setCustomer(redelivery.customer);
        setExpirationDate(redelivery.expirationDate ?? '');
        if (redelivery.location && redelivery.location.id !== selectedLocation?.id) {
          setSelectedLocation(redelivery.location);
        }
        setNotes(redelivery.notes);
        setRedeliveryDetails(
          (redelivery.redeliveryDetails ?? []).reduce((acc, val) => acc.set(val.id, val), new Map())
        );
        setRedeliveryNumber(redelivery.redeliveryNumber ?? '');
        setStartDate(redelivery.startDate ?? '');
        setTruckingCompanies(
          (redelivery.truckingCompanies ?? []).reduce((acc, val) => acc.set(val.id, val), new Map())
        );
      })
      .finally(() => setLoading(false));
  }, [id, getRedelivery, pathname, selectedLocation, setSelectedLocation]); /* do not remove pathname */

  const getRedeliveryObject = useCallback((): Redelivery => {
    return {
      customer,
      expirationDate,
      id: isNaN(id) ? null : id,
      location: selectedLocation,
      notes,
      redeliveryDetails: [...redeliveryDetails.values()].map(redeliveryDetail =>
        redeliveryDetail.id < 0 ? { ...redeliveryDetail, id: null } : redeliveryDetail
      ),
      redeliveryNumber,
      startDate,
      truckingCompanies: [...truckingCompanies.values()],
      units: [...units.values()]
    } as any;
  }, [
    customer,
    expirationDate,
    id,
    notes,
    redeliveryDetails,
    redeliveryNumber,
    selectedLocation,
    startDate,
    truckingCompanies,
    units
  ]);

  return (
    <RedeliveryContext.Provider
      value={{
        customer,
        setCustomer,
        id,
        expirationDate,
        setExpirationDate,
        getRedeliveryObject,
        loading,
        location: selectedLocation,
        setLocation: setSelectedLocation,
        original,
        notes,
        setNotes,
        redeliveryDetails,
        setRedeliveryDetails,
        redeliveryNumber,
        setRedeliveryNumber,
        redeliveryNumberRef,
        startDate,
        setStartDate,
        truckingCompanies,
        setTruckingCompanies,
        units,
        setUnits
      }}
    >
      <Outlet />
    </RedeliveryContext.Provider>
  );
};
